<template>
  <v-navigation-drawer
    v-model="drawer"
    location="right"
    temporary
    width="340"
    :color="drawerBg"
    class="theme-quick-drawer"
  >
    <!-- Header -->
    <div class="d-flex justify-space-between align-center px-4 py-3 border-bottom">
      <div class="d-flex align-center">
        <v-icon size="small" class="mr-2" :style="{ color: primaryColor }">
          fa-solid fa-palette
        </v-icon>
        <span class="font-weight-bold text-subtitle-2 text-white">
          Estilo Visual
        </span>
      </div>
      <v-btn
        icon="fa-solid fa-xmark"
        variant="text"
        size="x-small"
        color="white"
        @click="drawer = false"
      ></v-btn>
    </div>

    <div class="pa-4 drawer-scroll-content">
      <!-- Section 1: Presets -->
      <div class="mb-5">
        <div class="text-caption font-weight-bold text-grey-lighten-1 mb-2">
          ESTILOS OFICIALES
        </div>
        <div class="d-flex flex-column gap-2">
          <div
            v-for="(preset, key) in presets"
            :key="key"
            class="preset-item pa-3 mb-2 rounded-lg d-flex align-center justify-space-between"
            :class="{ 'active-preset-item': currentThemeId === key }"
            :style="currentThemeId === key ? { borderColor: preset.colors.primary } : {}"
            @click="selectPreset(key)"
          >
            <div>
              <div class="text-body-2 font-weight-bold text-white mb-1">
                {{ preset.name }}
              </div>
              <div class="d-flex align-center">
                <span
                  class="mini-swatch mr-1"
                  :style="{ backgroundColor: preset.colors.primary }"
                ></span>
                <span
                  class="mini-swatch mr-1"
                  :style="{ backgroundColor: preset.colors.navBar }"
                ></span>
                <span
                  class="mini-swatch mr-1"
                  :style="{ backgroundColor: preset.colors.drawerBg }"
                ></span>
                <span
                  class="mini-swatch"
                  :style="{ backgroundColor: preset.colors.background }"
                ></span>
              </div>
            </div>
            <v-icon
              v-if="currentThemeId === key"
              size="small"
              color="success"
            >
              fa-solid fa-check
            </v-icon>
          </div>
        </div>
      </div>

      <!-- Section 2: Quick Accent Color -->
      <div class="mb-5">
        <div class="text-caption font-weight-bold text-grey-lighten-1 mb-2">
          COLOR DE ACENTO
        </div>
        <div class="d-flex align-center flex-wrap gap-2 mb-2">
          <span
            v-for="color in quickPalette"
            :key="color"
            class="palette-circle"
            :style="{ backgroundColor: color }"
            :class="{ 'active-circle': primaryColor === color }"
            @click="updatePrimaryColor(color)"
          ></span>
          <div class="custom-color-btn ml-1">
            <input
              type="color"
              class="quick-color-input"
              :value="primaryColor"
              @input="updatePrimaryColor($event.target.value)"
            />
          </div>
        </div>
      </div>

      <!-- Section 3: Background Mode -->
      <div class="mb-5">
        <div class="text-caption font-weight-bold text-grey-lighten-1 mb-2">
          FONDO
        </div>
        <v-btn-toggle
          v-model="activeBgMode"
          density="compact"
          mandatory
          divided
          variant="outlined"
          class="w-100 mb-3"
        >
          <v-btn value="video" size="x-small" class="flex-grow-1 text-none">
            Video
          </v-btn>
          <v-btn value="preset" size="x-small" class="flex-grow-1 text-none">
            Textura
          </v-btn>
          <v-btn value="custom_url" size="x-small" class="flex-grow-1 text-none">
            Imagen
          </v-btn>
          <v-btn value="color" size="x-small" class="flex-grow-1 text-none">
            Sólido
          </v-btn>
        </v-btn-toggle>

        <!-- Wallpapers preview row if preset -->
        <div v-if="activeBgMode === 'preset'" class="d-flex gap-2 mb-3">
          <div
            v-for="wall in wallpaperPresets"
            :key="wall.id"
            class="quick-wall-thumb"
            :class="{ 'active-thumb': bgPresetId === wall.id }"
            :style="{ background: wall.cssImage }"
            :title="wall.name"
            @click="selectWallpaper(wall.id)"
          ></div>
        </div>

        <!-- Custom Image upload / URL if custom_url -->
        <div v-if="activeBgMode === 'custom_url'" class="mb-3">
          <v-file-input
            label="Subir foto de fondo"
            prepend-icon=""
            prepend-inner-icon="fa-solid fa-upload"
            density="compact"
            variant="outlined"
            accept="image/*"
            hide-details
            class="mb-2"
            @update:model-value="handleFileUpload"
            @change="handleFileUpload"
          ></v-file-input>
          <v-text-field
            v-model="customUrlInput"
            label="O ingresar URL directa"
            placeholder="https://..."
            density="compact"
            variant="outlined"
            prepend-inner-icon="fa-solid fa-link"
            hide-details
            class="mb-2"
            @update:model-value="updateCustomUrl"
          ></v-text-field>
        </div>

        <!-- Solid Background Color if color -->
        <div v-if="activeBgMode === 'color'" class="mb-3">
          <div class="text-caption text-grey-lighten-2 mb-2">Color del Fondo:</div>
          <div class="d-flex align-center flex-wrap gap-2 mb-2">
            <span
              v-for="scolor in solidColorPalette"
              :key="scolor"
              class="palette-circle"
              :style="{ backgroundColor: scolor }"
              :class="{ 'active-circle': backgroundColor === scolor }"
              @click="updateBackgroundColor(scolor)"
            ></span>
            <div class="custom-color-btn ml-1">
              <input
                type="color"
                class="quick-color-input"
                :value="backgroundColor"
                @input="updateBackgroundColor($event.target.value)"
              />
            </div>
          </div>
        </div>

        <!-- Dimmer Slider (only for textures and images) -->
        <div v-if="activeBgMode !== 'color'">
          <div class="d-flex justify-space-between text-caption text-grey-lighten-2 mb-1">
            <span>Opacidad del velo</span>
            <span class="font-weight-bold" :style="{ color: primaryColor }">
              {{ Math.round(overlayOpacity * 100) }}%
            </span>
          </div>
          <v-slider
            v-model="overlayOpacity"
            min="0.05"
            max="0.8"
            step="0.05"
            density="compact"
            hide-details
            :color="primaryColor"
            @update:model-value="updateOverlayOpacity"
          ></v-slider>
        </div>
      </div>

      <!-- Actions -->
      <v-divider class="my-4"></v-divider>
      <v-btn
        block
        size="small"
        class="mb-2 text-none font-weight-bold"
        :style="{ backgroundColor: primaryColor + ' !important', color: '#000 !important' }"
        :loading="isSaving"
        @click="saveTheme"
      >
        <v-icon start size="x-small">fa-solid fa-floppy-disk</v-icon>
        Guardar Configuración
      </v-btn>

      <v-btn
        block
        variant="text"
        size="small"
        class="text-none text-grey-lighten-1"
        to="/theme"
        @click="drawer = false"
      >
        <v-icon start size="x-small">fa-solid fa-sliders</v-icon>
        Ajustes Avanzados
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { WALLPAPER_PRESETS } from '../utils/themeHelper';

export default {
  name: 'ThemeDrawer',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'toast'],
  data() {
    return {
      wallpaperPresets: WALLPAPER_PRESETS,
      quickPalette: ['#DAA520', '#FFA400', '#00D2FF', '#4CAF50', '#E63946', '#9C27B0'],
      solidColorPalette: ['#000000', '#0B0F17', '#120E0A', '#18181B', '#092327', '#1A102F'],
      customUrlInput: '',
    };
  },
  computed: {
    drawer: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    presets() {
      return this.$store.getters['theme/themePresets'] || {};
    },
    currentTheme() {
      return this.$store.getters['theme/currentTheme'] || {};
    },
    currentThemeId() {
      return this.$store.getters['theme/themeId'];
    },
    primaryColor() {
      return this.$store.getters['theme/primaryColor'];
    },
    drawerBg() {
      return this.$store.getters['theme/drawerBg'];
    },
    backgroundColor() {
      return this.$store.getters['theme/backgroundColor'] || '#000000';
    },
    background() {
      return this.currentTheme.background || {};
    },
    activeBgMode: {
      get() {
        return this.background.mode || 'video';
      },
      set(val) {
        this.changeBgMode(val);
      },
    },
    bgPresetId() {
      return this.background.presetId || 'rhinos_gold_dark';
    },
    overlayOpacity: {
      get() {
        return typeof this.background.overlayOpacity === 'number'
          ? this.background.overlayOpacity
          : 0.3;
      },
      set(val) {
        this.updateOverlayOpacity(val);
      },
    },
    isSaving() {
      return this.$store.getters['theme/isSaving'];
    },
  },
  watch: {
    'background.customUrl'(newVal) {
      this.customUrlInput = newVal || '';
    },
  },
  mounted() {
    this.customUrlInput = this.background.customUrl || '';
  },
  methods: {
    async selectPreset(presetId) {
      await this.$store.dispatch('theme/applyPreset', presetId);
      this.$emit('toast', 'Estilo "' + this.presets[presetId].name + '" activado.');
    },
    async updatePrimaryColor(color) {
      await this.$store.dispatch('theme/updateCustomColor', {
        key: 'primary',
        value: color,
      });
    },
    async updateBackgroundColor(color) {
      await this.$store.dispatch('theme/updateCustomColor', {
        key: 'background',
        value: color,
      });
    },
    async changeBgMode(mode) {
      await this.$store.dispatch('theme/updateBackground', { mode });
    },
    async selectWallpaper(presetId) {
      await this.$store.dispatch('theme/updateBackground', {
        mode: 'preset',
        presetId,
      });
    },
    async updateCustomUrl(url) {
      await this.$store.dispatch('theme/updateBackground', {
        mode: 'custom_url',
        customUrl: url,
      });
    },
    async updateOverlayOpacity(val) {
      await this.$store.dispatch('theme/updateBackground', {
        overlayOpacity: parseFloat(val),
      });
    },
    handleFileUpload(eventOrFiles) {
      let file = null;
      if (eventOrFiles instanceof File) {
        file = eventOrFiles;
      } else if (Array.isArray(eventOrFiles) && eventOrFiles.length > 0) {
        file = eventOrFiles[0];
      } else if (eventOrFiles?.target?.files?.length > 0) {
        file = eventOrFiles.target.files[0];
      }
      if (!file) return;
      if (file.size > 2.5 * 1024 * 1024) {
        this.$emit('toast', 'La imagen supera los 2.5MB permitidos.', 'warning');
        return;
      }
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const dataUrl = evt.target.result;
        this.customUrlInput = dataUrl;
        await this.$store.dispatch('theme/updateBackground', {
          mode: 'custom_url',
          customUrl: dataUrl,
        });
        this.$emit('toast', 'Fondo personalizado cargado.');
      };
      reader.readAsDataURL(file);
    },
    async saveTheme() {
      const res = await this.$store.dispatch('theme/saveThemeToBackend');
      if (res && res.success === false) {
        this.$emit(
          'toast',
          'Guardado localmente. (Error al sincronizar con servidor)',
          'warning'
        );
      } else {
        this.$emit('toast', 'Tema guardado y sincronizado exitosamente.');
      }
    },
  },
};
</script>

<style scoped>
.theme-quick-drawer {
  z-index: 9999 !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.drawer-scroll-content {
  max-height: calc(100vh - 60px);
  overflow-y: auto;
}

.preset-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s;
}

.preset-item:hover {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(255, 255, 255, 0.3);
}

.active-preset-item {
  border-width: 2px !important;
  background: rgba(255, 255, 255, 0.08);
}

.mini-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.palette-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.15s;
}

.palette-circle:hover {
  transform: scale(1.15);
}

.active-circle {
  border-color: #fff !important;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.6);
}

.custom-color-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.quick-color-input {
  -webkit-appearance: none;
  border: none;
  width: 40px;
  height: 40px;
  position: absolute;
  top: -7px;
  left: -7px;
  cursor: pointer;
}

.quick-wall-thumb {
  flex: 1;
  height: 40px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: transform 0.2s;
}

.quick-wall-thumb:hover {
  transform: scale(1.05);
}

.active-thumb {
  border: 2px solid #4caf50 !important;
}

.gap-2 {
  gap: 8px;
}
</style>
